import React, { useState, useEffect } from 'react';
import { BringzzIcon, BringzzText } from "@bringzz/components";
import useRequest from '../hooks/useRequest';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { USER_ROLES } from '../utils/constants';
import { toast } from 'react-toastify';

const RecentNotifications = () => {
    const { sendRequest, loading } = useRequest();
    const { user } = useAuth();
    const { navigate } = useNavigation();
    const [fetchedNotifications, setFetchedNotifications] = useState([]);

    useEffect(() => {
        const fetchNotifications = async () => {
            if (user && user.id) {
                try {
                    const response = await sendRequest({
                        method: 'GET',
                        url: '/module/notification/fetchNotificationsByUser',
                    });

                    if (response?.status === 'success') {
                        // Sort notifications by date (newest first) and take first 3
                        const sortedNotifications = (response.data || []).sort((a, b) => {
                            const dateA = parseNotificationDateForSort(a.created);
                            const dateB = parseNotificationDateForSort(b.created);
                            return dateB - dateA; // Newest first
                        }).slice(0, 3);
                        setFetchedNotifications(sortedNotifications);
                    } else if (response?.status === 'error' && response?.message === 'No notifications found') {
                        setFetchedNotifications([]);
                    }
                } catch (error) {
                    console.error('Error fetching notifications:', error);
                }
            }
        };

        fetchNotifications();
    }, [user?.id]);

    const handleNotificationClick = async (notification) => {
        // Navigate to notification page for detailed view
        navigate('/notifications');
    };

    const getOrderDetailsUrl = (uuid) => {
        if (user?.userRole === USER_ROLES.PHARMACY_ROLE) {
            return `/settings/pharmacy/orders/view/${uuid}`;
        } else if (user?.userRole === USER_ROLES.SHOPPER_ROLE) {
            return `/settings/shopper/orders/view/${uuid}`;
        } else if (user?.userRole === USER_ROLES.CONSUMER_ROLE) {
            return `/settings/consumer/orders/view/${uuid}`;
        }
        return `/settings/orders/view/${uuid}`;
    };

    const parseNotificationContent = (content) => {
        try {
            const parsed = JSON.parse(content);
            return parsed;
        } catch {
            return { message: content };
        }
    };

    const parseNotificationDateForSort = (dateString) => {
        try {
            const [date, time] = dateString.split(' ');
            const [day, month, year] = date.split('.');
            const [hours, minutes] = time.split(':');
            return new Date(Date.UTC(year, month - 1, day, hours, minutes));
        } catch {
            return new Date(0);
        }
    };

    const formatNotificationDate = (dateString) => {
        try {
            const [date, time] = dateString.split(' ');
            const [day, month, year] = date.split('.');
            const [hours, minutes] = time.split(':');
            const dateObj = new Date(Date.UTC(year, month - 1, day, hours, minutes));

            const now = new Date();
            const diff = now - dateObj;
            const hours_diff = Math.floor(diff / (1000 * 60 * 60));

            if (hours_diff < 1) {
                const minutes_diff = Math.floor(diff / (1000 * 60));
                if (minutes_diff < 1) return 'Just now';
                return `${minutes_diff}m ago`;
            } else if (hours_diff < 24) {
                return `${hours_diff}h ago`;
            } else {
                const days_diff = Math.floor(hours_diff / 24);
                if (days_diff === 1) return 'Yesterday';
                if (days_diff < 7) return `${days_diff}d ago`;
                if (days_diff < 30) return `${Math.floor(days_diff / 7)}w ago`;
                return dateObj.toLocaleDateString();
            }
        } catch {
            return dateString;
        }
    };

    const getNotificationIcon = (notificationType) => {
        switch (notificationType) {
            case 'ORDER_REQUEST':
                return 'IconShoppingBag';
            case 'MESSAGE':
                return 'IconMessage';
            default:
                return 'IconBell';
        }
    };

    // Calculate total and unread counts from all notifications (not just displayed 3)
    const [allNotifications, setAllNotifications] = useState([]);

    useEffect(() => {
        const fetchAllNotifications = async () => {
            if (user && user.id) {
                try {
                    const response = await sendRequest({
                        method: 'GET',
                        url: '/module/notification/fetchNotificationsByUser',
                    });

                    if (response?.status === 'success') {
                        setAllNotifications(response.data || []);
                    }
                } catch (error) {
                    console.error('Error fetching all notifications count:', error);
                }
            }
        };
        fetchAllNotifications();
    }, [user?.id]);

    const totalCount = allNotifications.length;
    const unreadCount = allNotifications.filter(n => n.read === 0).length;

    return (
        <div className="px-4 py-2 bg-white">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <BringzzText tag="h3" className="text-lg font-semibold">
                        Recent notifications
                    </BringzzText>
                    <BringzzText tag="p" className="text-xs text-gray-500">
                        {totalCount} total • {unreadCount} unread
                    </BringzzText>
                </div>
                <button
                    onClick={() => navigate('/notifications')}
                    className="text-magic-lilac hover:text-magic-lilac/80 text-sm font-medium"
                >
                    View all
                </button>
            </div>

            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                            <div className="flex items-start space-x-3">
                                <div className="w-[32px] h-[32px] rounded-full bg-gray-200 animate-pulse"></div>
                                <div className="flex-1">
                                    <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4 mb-2"></div>
                                    <div className="h-3 bg-gray-200 animate-pulse rounded w-1/2 mb-1"></div>
                                    <div className="h-3 bg-gray-200 animate-pulse rounded w-1/4"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : fetchedNotifications.length === 0 ? (
                <div className="text-center py-8">
                    <BringzzIcon
                        size="32"
                        icon="IconBellRinging"
                        folder="LineIcons"
                        className="mx-auto mb-2 text-gray-400"
                    />
                    <BringzzText className="text-gray-500">
                        No notifications
                    </BringzzText>
                </div>
            ) : (
                <div className="space-y-3">
                    {fetchedNotifications.map((notification, index) => {
                        const parsedContent = parseNotificationContent(notification.content || '{}');
                        const isUnread = notification.read === 0;

                        return (
                            <div
                                key={`recent-${notification.id || index}`}
                                className={`relative bg-white rounded-lg shadow-sm border border-gray-200 p-3 cursor-pointer transition-all hover:shadow-md ${
                                    isUnread ? 'border-l-4 border-l-magic-lilac' : ''
                                }`}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <div className="flex items-start space-x-3">
                                    <div className={`p-1.5 rounded-full ${isUnread ? 'bg-magic-lilac bg-opacity-20' : 'bg-gray-100'}`}>
                                        <BringzzIcon
                                            size="16"
                                            icon={getNotificationIcon(notification.notification_type)}
                                            folder="LineIcons"
                                            className={isUnread ? 'text-magic-lilac' : 'text-gray-600'}
                                        />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-0.5">
                                            <BringzzText
                                                tag="h4"
                                                className={`font-semibold text-xs ${isUnread ? 'text-black' : 'text-gray-800'}`}
                                            >
                                                {notification.title}
                                            </BringzzText>
                                            {isUnread && (
                                                <div className="w-1.5 h-1.5 bg-magic-lilac rounded-full ml-2"></div>
                                            )}
                                        </div>

                                        {/* Order details for ORDER_REQUEST type */}
                                        {notification.notification_type === 'ORDER_REQUEST' && parsedContent.consumer && (
                                            <BringzzText className="text-xs text-gray-600 mb-0.5">
                                                Order from {parsedContent.consumer} • €{parsedContent.total}
                                            </BringzzText>
                                        )}

                                        {/* Generic content for other notification types */}
                                        {notification.notification_type !== 'ORDER_REQUEST' && (
                                            <BringzzText className="text-xs text-gray-600 mb-0.5 line-clamp-1">
                                                {typeof parsedContent === 'string' ? parsedContent : parsedContent.message || 'No content'}
                                            </BringzzText>
                                        )}

                                        <BringzzText className="text-xs text-gray-400">
                                            {formatNotificationDate(notification.created)}
                                        </BringzzText>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RecentNotifications;